"use client";

/**
 * characterViews.tsx — キャラの中身を画面に起こす部品
 *
 * キャラクター確認画面と、進行の画面のキャラ情報で使い回す。中身は GET /api/character/detail の応答。
 */

export interface Valued {
  key: string;
  label: string;
  value: number;
}

/** 判定実数値 32 種の系統・綴り・表記名。並びは画面に出す順 */
export const JUDGMENT_GROUPS: { label: string; keys: [string, string][] }[] = [
  {
    label: "戦闘系",
    keys: [["accuracy", "命中"], ["evasion", "回避"], ["critical", "クリティカル"], ["physical_defense", "物理防御"], ["magic_defense", "魔法防御"], ["physical_penetration", "物理貫通"], ["magic_penetration", "魔法貫通"], ["prayer", "祈祷"]],
  },
  {
    label: "技能系",
    keys: [["crafting", "製作"], ["compounding", "調合"], ["cooking", "料理"], ["gathering", "採取"], ["lockpicking", "解錠"], ["appraisal", "鑑定"], ["repair", "修繕"], ["performance", "演奏"]],
  },
  {
    label: "対人系",
    keys: [["persuasion", "説得"], ["intimidation", "威圧"], ["deception", "欺瞞"], ["insight", "看破"], ["healing", "治癒"], ["purification", "浄化"], ["blessing", "祝福"], ["encouragement", "鼓舞"]],
  },
  {
    label: "生存系",
    keys: [["detection", "探知"], ["stealth", "隠密"], ["tracking", "追跡"], ["vigilance", "警戒"], ["recovery", "回復"], ["swimming", "水泳"], ["camping", "野営"], ["butchering", "解体"]],
  },
];

/** 判定実数値の綴り → 表記名。並びは JUDGMENT_GROUPS の順 */
export const JUDGMENT_LABEL: Record<string, string> = Object.fromEntries(JUDGMENT_GROUPS.flatMap((group) => group.keys));

/** 判定実数値を 32 種の並びと表記名に揃える。32 種に無い綴りは出さない */
export function judgmentRows(values: Record<string, number> | Valued[]): Valued[] {
  const get = (key: string) => (Array.isArray(values) ? values.find((one) => one.key === key)?.value : values[key]);
  return Object.keys(JUDGMENT_LABEL)
    .map((key) => ({ key, label: JUDGMENT_LABEL[key], value: get(key) }))
    .filter((row): row is Valued => typeof row.value === "number");
}

/** 持つにも着けるにも筋力が要る積載の線 */
export const HEAVY_BULK = 20;

/** 持つのに要る筋力。積載 20 以上の物は 積載 × 1.5（切り上げ）、ほかは 0 */
export function strengthToCarry(bulk: number): number {
  return bulk >= HEAVY_BULK ? Math.ceil(bulk * 1.5) : 0;
}

/** 積載の上限の土台。上限 ＝ 70 ＋ 筋力 ＋ 袋の加算 */
export const LOAD_BASE = 70;

/** 基礎実数値の筋力。持たないときは null */
export function strengthOf(status: Detail["status"]): number | null {
  return status?.base.find((row) => row.key === "str")?.value ?? null;
}

export interface Money {
  label: string;
  amount: number;
}

export interface ItemView {
  id: string;
  name: string;
  count: number;
  equipped: boolean;
  category: string[];
  /** 分類の綴り。category と同じ並び。所持品の絞り込みと照らす */
  category_keys?: string[];
  bulk: number;
  price: Money[];
  summary: string;
  feature: string;
  effect: string;
  use: { description: string; text: string } | null;
  equip: {
    type: string;
    /** 装備種の綴り。層の accepts と照らす */
    type_key?: string;
    modifiers: string[];
    attack: string | null;
    grants: { name: string; description: string }[];
    durability: { max: number; current: number } | null;
    locked: boolean;
  } | null;
}

export interface SkillView {
  slug: string;
  name: string;
  passive: boolean;
  kind: string;
  summary: string;
  effect: string;
  cost: string;
  battle: { description: string; cooldown: number } | null;
  non_battle: string | null;
  /** 本人の技なら true。体得から持てるスキルなら false */
  own?: boolean;
  /** 体得から持てるスキルの、元の体得のスラッグ */
  mastery?: string[];
  /** 元の体得の表記名。mastery と同じ並び */
  mastery_names?: string[];
  /** 戦闘に持っていくスキルなら true */
  loadout?: boolean;
  /** 要る装備種を 1 つも着けていないとき、要る装備種の表記名の並び。使えるときは空 */
  missing_equipment?: string[];
}

export interface MasteryView {
  slug: string;
  name: string;
  difficulty: number | null;
  skills: number;
  skill_names?: string[];
  skill_slugs?: string[];
  summary?: string;
  way?: string;
  unique?: string;
}

export interface Detail {
  slug: string;
  name: string;
  profile: string | null;
  status: {
    base: Valued[];
    judgment: Valued[];
    hp: { current: number; max: number };
    pools: { label: string; color?: string | null; current: number; max: number }[];
    resistance: Valued[];
    damage_taken: Valued[];
    load: { current: number; max: number };
    money: Money[];
  } | null;
  /** layers の accepts は受け入れる装備種の綴り、capacity は入る数 */
  equipment: { part: string; /** モディファイアが付いて、付け外しの止まった部位 */ blocked?: boolean; layers: { layer: string; items: string[]; ids?: string[]; accepts?: string[]; capacity?: number }[] }[];
  /**
   * 装備の付け替えと持ち物の破棄の予約。open は今決められるか、off・on・drop は外す物・着ける物・捨てる物の ID、
   * equipment は付け替えた後の装備部位で、予約が無ければ null
   */
  equip_plan?: { open: boolean; off: string[]; on: string[]; drop?: string[]; equipment: Detail["equipment"] | null } | null;
  items: ItemView[];
  skills: SkillView[];
  mastery?: MasteryView[];
  loadout_max?: number;
  /** 振り分けポイントと、振った成長値。ステータスを持たないキャラは null */
  growth?: { points: number; total: number; cap: number; values: Record<string, number> } | null;
  /** 人物の今の頭。種族・性別・現在地・現在時刻・移動手段 */
  head?: { label: string; value: string }[];
  /** 今付いている状態 */
  modifiers?: ModifierView[];
  /** 食べた・飲んだ・起きてからの時間。記録の無いキャラは空 */
  needs?: { label: string; hours: number | null }[];
  relations?: RelationView[];
  /** PC の持つイベント。同行者は null */
  events?: EventView[] | null;
  /** PC の同行者の名の並び。同行者の中身には付かない */
  companions?: { slug: string; name: string }[];
}

export interface ModifierView {
  slug: string;
  name: string;
  kind: string;
  summary: string;
  stacks: number;
  since: string | null;
  remaining: string;
  worsen: string | null;
}

export interface RelationView {
  ref: string;
  name: string;
  relation: string;
  image: string;
  history: string[];
  affinity: number | null;
}

export interface EventView {
  id: string;
  name: string;
  state: string;
  state_label: string;
  ended: boolean;
  seen: string;
  history: { from: string | null; to: string | null; text: string }[];
  started: string;
}

/** メインの md を画面の形に起こす。見出し・箇条書き・文だけを扱う */
export function Profile({ text }: { text: string }) {
  const nodes: React.ReactNode[] = [];
  let bullets: string[] = [];
  const flush = () => {
    if (bullets.length === 0) return;
    nodes.push(
      <ul className="review-list" key={`ul-${nodes.length}`}>
        {bullets.map((line, index) => (
          <li key={index}>{line}</li>
        ))}
      </ul>
    );
    bullets = [];
  };
  const plain = (line: string) => line.replace(/\[([^\]]+)\]\([^)]*\)/g, "$1");

  for (const raw of text.split("\n")) {
    const line = raw.trimEnd();
    if (line.startsWith("- ")) {
      bullets.push(plain(line.slice(2)));
      continue;
    }
    flush();
    if (line === "" || line.startsWith("# ")) continue;
    if (line.startsWith("### ")) nodes.push(<h4 className="review-h4" key={nodes.length}>{line.slice(4)}</h4>);
    else if (line.startsWith("## ")) nodes.push(<h3 className="review-h3" key={nodes.length}>{line.slice(3)}</h3>);
    else nodes.push(<p className="review-p" key={nodes.length}>{plain(line)}</p>);
  }
  flush();
  return <div>{nodes}</div>;
}

export function ValueGrid({ rows, digits }: { rows: Valued[]; digits?: number }) {
  return (
    <div className="review-grid">
      {rows.map((row) => (
        <div className="review-cell" key={row.key}>
          <span>{row.label}</span>
          <strong>{digits === undefined ? row.value : row.value.toFixed(digits)}</strong>
        </div>
      ))}
    </div>
  );
}

/**
 * plain を付けると、名の横の 装備中・分類 を出さない。tag は名の横に添える札。children は開いた中の末尾の、積載と値段の行の右に置く。
 * str は持ち主の筋力。要る筋力に届かないとき、要る筋力を警告の色で出す
 */
export function ItemCard({ item, plain, tag, str, children }: { item: ItemView; plain?: boolean; tag?: string; str?: number | null; children?: React.ReactNode }) {
  const need = strengthToCarry(item.bulk);
  const short = need > 0 && typeof str === "number" && str < need;
  return (
    <details className="review-card">
      <summary>
        <span className="review-card-name">
          {item.name}
          {item.count > 1 ? ` ×${item.count}` : ""}
          {tag ? <span className="pl-card-tag">{tag}</span> : null}
        </span>
        {plain ? null : (
          <span className="review-card-meta">
            {item.equipped ? "装備中・" : ""}
            {item.category.join("・")}
          </span>
        )}
      </summary>
      {item.summary ? <p className="review-p">{item.summary}</p> : null}
      {item.feature ? <p className="review-p review-dim">{item.feature}</p> : null}
      {item.effect ? <p className="review-p">{item.effect}</p> : null}
      {item.use ? (
        <div className="review-block">
          <h4 className="review-h4">使用時</h4>
          <p className="review-p">{item.use.text || item.use.description}</p>
        </div>
      ) : null}
      {item.equip ? (
        <div className="review-block">
          <h4 className="review-h4">装備時（{item.equip.type}）</h4>
          {item.equip.modifiers.length > 0 ? <p className="review-p">{item.equip.modifiers.join("・")}</p> : null}
          {item.equip.attack ? <p className="review-p">通常攻撃：{item.equip.attack}</p> : null}
          {item.equip.grants.map((skill) => (
            <p className="review-p" key={skill.name}>
              付与スキル「{skill.name}」：{skill.description}
            </p>
          ))}
          {item.equip.durability ? (
            <p className="review-p review-dim">
              耐久 {item.equip.durability.current} / {item.equip.durability.max}
            </p>
          ) : null}
        </div>
      ) : null}
      <div className="review-foot">
        <div className="review-facts">
          <div className="review-cell">
            <span>積載</span>
            <strong>
              {item.bulk}
              {item.count > 1 ? `（${item.count} 個で ${item.bulk * item.count}）` : ""}
            </strong>
          </div>
          {need > 0 ? (
            <div className="review-cell">
              <span>要る筋力</span>
              <strong className={short ? "review-warn" : ""}>{need}</strong>
            </div>
          ) : null}
          {item.price.length > 0 ? (
            <div className="review-cell">
              <span>値段</span>
              <strong>{item.price.map((one) => `${one.amount} ${one.label}`).join("・")}</strong>
            </div>
          ) : null}
        </div>
        {children}
      </div>
    </details>
  );
}

export function SkillCard({ skill }: { skill: SkillView }) {
  const missing = skill.missing_equipment ?? [];
  return (
    <details className="review-card">
      <summary>
        <span className="review-card-name">{skill.name}</span>
        <span className="review-card-meta">
          {skill.passive ? "パッシブ" : "スキル"}
          {skill.kind ? `・${skill.kind}` : ""}
          {skill.loadout ? "・戦闘に持っていく" : ""}
          {missing.length > 0 ? <span className="review-warn">・使えない</span> : null}
        </span>
      </summary>
      {missing.length > 0 ? <p className="review-p review-warn">使えない：{missing.join("・")}を着けていない</p> : null}
      {skill.summary ? <p className="review-p">{skill.summary}</p> : null}
      {skill.effect ? <p className="review-p">{skill.effect}</p> : null}
      {skill.battle ? (
        <div className="review-block">
          <h4 className="review-h4">戦闘時</h4>
          <p className="review-p">{skill.battle.description}</p>
          {skill.battle.cooldown > 0 ? <p className="review-p review-dim">クールダウン {skill.battle.cooldown} ターン</p> : null}
        </div>
      ) : null}
      {skill.non_battle ? (
        <div className="review-block">
          <h4 className="review-h4">戦闘の外</h4>
          <p className="review-p">{skill.non_battle}</p>
        </div>
      ) : null}
      {skill.cost ? <p className="review-p review-dim">消費：{skill.cost}</p> : null}
      {(skill.mastery_names ?? []).length > 0 ? <p className="review-p review-dim">{(skill.mastery_names ?? []).join("・")}で体得</p> : null}
    </details>
  );
}


/** 能力のタブ。活量・基礎実数値・判定実数値・耐性・被弾を並べる */
export function StatusView({ status }: { status: NonNullable<Detail["status"]> }) {
  return (
    <div>
      <h3 className="review-h3">活量</h3>
      <div className="review-grid">
        <div className="review-cell">
          <span>HP</span>
          <strong>{status.hp.max}</strong>
        </div>
        {status.pools.map((pool) => (
          <div className="review-cell" key={pool.label}>
            <span>{pool.label}</span>
            <strong>{pool.max}</strong>
          </div>
        ))}
        <div className="review-cell">
          <span>積載</span>
          <strong>
            {status.load.current} / {status.load.max}
          </strong>
        </div>
        {status.money.map((one) => (
          <div className="review-cell" key={one.label}>
            <span>持ち金 {one.label}</span>
            <strong>{one.amount}</strong>
          </div>
        ))}
      </div>
      <h3 className="review-h3">基礎実数値</h3>
      <ValueGrid rows={status.base} />
      <h3 className="review-h3">判定実数値</h3>
      <ValueGrid rows={judgmentRows(status.judgment)} />
      <h3 className="review-h3">耐性</h3>
      <ValueGrid rows={status.resistance.map((row) => ({ ...row, value: 2 - row.value }))} digits={1} />
      <h3 className="review-h3">被弾</h3>
      <ValueGrid rows={status.damage_taken} digits={2} />
    </div>
  );
}

/** 装備のタブ。部位ごとに層と着けている物を並べる */
export function EquipmentView({ equipment }: { equipment: Detail["equipment"] }) {
  return (
    <div>
      {equipment.map((part) => (
        <div key={part.part}>
          <h3 className="review-h3">{part.part}</h3>
          {part.layers.map((layer, index) => (
            <div className="review-row" key={index}>
              <span>{layer.layer}</span>
              <strong className={layer.items.length > 0 ? "" : "review-dim"}>{layer.items.length > 0 ? layer.items.join("・") : "—"}</strong>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/** 持ち物のタブ。装備品と所持品に分けて並べる */
export function ItemsView({ items }: { items: ItemView[] }) {
  return (
    <div>
      <h3 className="review-h3">装備品</h3>
      {items.filter((item) => item.equipped).map((item) => <ItemCard item={item} key={item.id} />)}
      <h3 className="review-h3">所持品</h3>
      {items.filter((item) => !item.equipped).map((item) => <ItemCard item={item} key={item.id} />)}
    </div>
  );
}
